import { v4 as uuidv4 } from 'uuid';
import { logExceptionOnce } from '@hungryroot/o11y';
import { getAnonymousId } from '@hungryroot/segment';
import { withTimeoutOrNull } from '@hungryroot/segment/with-timeout';

export const SALAD_SHOOTER_STREAM_ID_SESSION_KEY = 'hr.salad-shooter.stream-id';

/**
 * How long we wait for Segment's anonymous ID to become available before
 * falling back to a locally-generated stream ID. Segment is loaded
 * asynchronously (and may be blocked by ad blockers / slow networks); we
 * don't want Salad Shooter ingest to stall indefinitely.
 */
const ANONYMOUS_ID_TIMEOUT_MS = 2_000;

let anonymousIdInitPromise: Promise<string | null> | null = null;

function generateFallbackStreamId() {
  return 'stream_id_' + uuidv4();
}

function readStoredStreamId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return sessionStorage.getItem(SALAD_SHOOTER_STREAM_ID_SESSION_KEY);
  } catch {
    // Safari private mode / blocked storage
    return null;
  }
}

function writeStoredStreamId(streamId: string) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(SALAD_SHOOTER_STREAM_ID_SESSION_KEY, streamId);
  } catch {
    // No-op: avoid crashing when sessionStorage is unavailable
  }
}

/**
 * Awaits Segment's anonymous ID (with a timeout) and persists it as the
 * Salad Shooter stream ID so subsequent sync reads return the same ID used
 * by Segment for event correlation. Memoized for the lifetime of the page.
 *
 * Returns `null` if Segment is unavailable/blocked or does not resolve in
 * time — callers should fall back to {@link getStreamId}, which will emit
 * a locally-generated identifier.
 */
function initializeAnonymousId(): Promise<string | null> {
  if (anonymousIdInitPromise !== null) {
    return anonymousIdInitPromise;
  }

  anonymousIdInitPromise = (async () => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const anonymousId = await withTimeoutOrNull(
        getAnonymousId(),
        ANONYMOUS_ID_TIMEOUT_MS,
        'Segment anonymous id'
      );
      if (anonymousId) {
        writeStoredStreamId(anonymousId);
        return anonymousId;
      }
    } catch (error) {
      logExceptionOnce(error, {
        key: 'salad-shooter.getStreamId.anonymousIdInit',
      });
    }
    return null;
  })();

  return anonymousIdInitPromise;
}

/**
 * Synchronously returns the current Salad Shooter stream ID.
 *
 * - Prefers Segment's anonymous ID when it has been initialized (either via
 *   {@link getStreamIdAsync} or the module-load side effect below). This is
 *   the canonical identifier used across Segment analytics, so downstream
 *   pipelines can join Salad Shooter events with Segment events without a
 *   parallel ID scheme.
 * - Falls back to a locally-generated `stream_id_<uuid>` if Segment is not
 *   yet ready, is disabled, or is blocked. The fallback is persisted for
 *   the session so repeat sync reads stay stable; once Segment's anonymous
 *   ID resolves it overwrites this value so subsequent events use the
 *   canonical ID.
 */
export function getStreamId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = readStoredStreamId();
  if (stored !== null) {
    return stored;
  }

  const fallback = generateFallbackStreamId();
  writeStoredStreamId(fallback);
  return fallback;
}

/**
 * Awaits Segment's anonymous ID and returns the resulting stream ID. Use
 * this in async contexts (like Salad Shooter ingest) where we want to
 * guarantee the canonical Segment identifier is used whenever possible.
 *
 * If Segment is disabled, blocked, or slow, this falls back to the same
 * locally-generated `stream_id_<uuid>` emitted by {@link getStreamId} so
 * ingest continues to work — at the cost of a session whose events cannot
 * be joined to Segment.
 */
export async function getStreamIdAsync(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }
  await initializeAnonymousId();
  return getStreamId();
}

// Kick off Segment anonymous-ID initialization as early as possible so that
// most sync consumers of `getStreamId` observe the canonical ID rather than
// the fallback. Swallowed errors are already logged inside
// `initializeAnonymousId`.
if (typeof window !== 'undefined') {
  try {
    initializeAnonymousId();
  } catch {
    // No-op: avoid crashing on import
  }
}
